import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import IconArrowRight from '@remote-com/norma/icons/arrow-right.svg';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { PrimaryCTA, PrimaryCTAButton } from 'remote-com-shared/components/CallToAction/Refreshed';
import { ClientLogosSlider } from 'remote-com-shared/components/ClientLogos/ClientLogosSlider';
import { DocumentHead } from 'remote-com-shared/components/DocumentHead';
import HeaderContext from 'remote-com-shared/components/Header/context';
import MenuEntry from 'remote-com-shared/components/HeaderNavigation/MenuEntry';
import MenuItem from 'remote-com-shared/components/HeaderNavigation/MenuItem';
import {
  NavigationPrimary,
  NavigationSecondary,
} from 'remote-com-shared/components/HeaderNavigation/styles/Navigation';
import { TwoStepForm, TwoStepFormModalButton } from 'remote-com-shared/components/TwoStepForm';
import { reasonOptions } from 'remote-com-shared/data';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { ThemeProvider, useTheme } from 'styled-components';

import { PricingSectionRefreshed, NewPricingSection } from './PricingSection';
import { PrimaryCTASection, CtaButton } from './PrimaryCtaSection';
import { Testimonials, NewTestimonials } from './Testimonials';
import { NewWhyRemoteSection } from './WhyRemoteSection';
import {
  HeroBackgroundIllustration,
  CustomHeroWrapper,
  CustomHeroImageWrapper,
  Main,
  HeroDescription,
  HeaderCTAButton,
  GetStartedHero,
  GetStartedFeaturesList,
  GetStartedFeaturesListEntry,
  BadgesContainer,
  RecognitionBadges,
  Badge,
  TrustpilotRatingWrapper,
  RatingStars,
  StyledTrustpilotLogo,
  StyledFrenchTrustpilotLogo,
} from './styles/Refreshed';

import { SinglePageScrollLayout } from '@/layouts/SinglePageScrollLayout';
import CycleText from '@/src/components/CycleText';
import { Header } from '@/src/components/Header';
import { SlidesCarouselSection } from '@/src/components/OverviewSection/OverviewSectionCarousel';
import { WhyRemoteSection } from '@/src/components/WhyRemote';
import SiteHeader from '@/src/components/shared/SiteHeader';
import { transformProperties } from '@/src/domains/prismic/helpers';

const OMITTED_NAVIGATION_ITEM = [];

const TrustpilotRating = ({ rating, frenchLocale }) => (
  <TrustpilotRatingWrapper>
    <RatingStars $background aria-hidden />
    <RatingStars
      $rawValue={rating.toString()}
      aria-label={`Trustpilot rating is ${rating} out of 5.`}
    />
    {frenchLocale ? <StyledFrenchTrustpilotLogo /> : <StyledTrustpilotLogo />}
  </TrustpilotRatingWrapper>
);

export const BadgesSection = ({ badges: rawBadges, trustpilotRating, frenchLocale }) => {
  const badges = rawBadges.filter(({ illustration }) => !!illustration);

  return (
    <BadgesContainer>
      <RecognitionBadges>
        {badges.map(({ illustration }, index) => {
          const { url, alt } = illustration;
          return (
            <Badge key={index}>
              <Image alt={alt || ''} src={url} quality="100" layout="fill" />
            </Badge>
          );
        })}
      </RecognitionBadges>
      {trustpilotRating && (
        <TrustpilotRating rating={trustpilotRating} frenchLocale={frenchLocale} />
      )}
    </BadgesContainer>
  );
};

const CustomHeroImage = (props) => (
  <CustomHeroWrapper data-show-custom-hero={false}>
    <span />
    <CustomHeroImageWrapper>
      <img alt=" " {...props} />
    </CustomHeroImageWrapper>
  </CustomHeroWrapper>
);

export function GetStartedNewView({
  clientLogosView,
  clientTestimonialsView,
  pricingOverview,
  getStartedView,
  promoCodeLandingPage = false,
  promoCodePageDetailView = {},
  hubspotFormDefinition,
  twoStepGetStartedHubspotFormDefinition,
  heroComponent,
  seoMeta,
  disablePrimaryCTAIcon,
  locale,
}) {
  // For paid promo code landing pages
  const {
    promo_code: promoCode,
    callout_text: calloutText,
    redirect_url: redirectURL,
  } = promoCodePageDetailView;

  const router = useRouter();
  const { asPath: fullPathname } = router;

  if (promoCodeLandingPage && typeof window !== 'undefined' && fullPathname !== redirectURL) {
    router.push(redirectURL, undefined, { shallow: true });
  }
  // ab test fr-fr
  const frenchOrSpanishLocale = locale === 'fr-fr' || locale === 'es-es';

  const headerRef = useRef(null);
  const heroRef = useRef(null);
  const scheduleDemoReasonFields = reasonOptions.slice(0, 2);

  const {
    headerNavigationItems: defaultHeaderNavigationItems,
    headerTalkWithUsButtonLabel,
    heroTitle,
    heroCycleEntries,
    heroDescription,
    heroFeatures: sourceHeroFeatures,
    heroBackgroundImage,
    badges,
    trustpilotRating,
    clientLogosTitle,
    pricingLabel,
    pricingTitle,
    ctaTitle,
    ctaButtonLabel,
    themeColorTone,
    getStartedFormTitle,
    getStartedFormDescription,
    learnMoreLabel,
  } = transformProperties(getStartedView);
  const theme = useTheme();

  const heroFeatures = (sourceHeroFeatures || []).filter(({ feature }) => !!feature);
  const headerNavigationItems = defaultHeaderNavigationItems.filter(
    ({ fragment }) => !OMITTED_NAVIGATION_ITEM.includes(fragment)
  );

  const { body: whyRemoteSliceResults, body1: overviewSectionSliceResults } = getStartedView;
  const whyRemoteSlices = whyRemoteSliceResults || [];
  const overviewSectionSlices = overviewSectionSliceResults || [];

  const heroCycleEntriesText = (heroCycleEntries || [])
    .map((entry) => entry.text)
    .filter((text) => !!text);
  const accessibleTitle = `${heroTitle} ${heroCycleEntriesText.join(', ')}`.replace(/\s?\n/g, ' ');

  const overviewSliceData = overviewSectionSlices.find(({ type }) => type === 'overview_section');
  const {
    primary: {
      how_does_it_work_label: overviewLabel,
      how_does_it_work_title: overviewTitle,
      how_does_it_work_description: overviewDescription,
    } = {},
    fields: overviewSections,
  } = overviewSliceData || {};
  const whyRemoteSliceData = whyRemoteSlices.find(({ type }) => type === 'why_remote');
  const { primary: whyRemotePrimary, fields: whyRemoteReasons } = whyRemoteSliceData || {};

  const documentSeo = seoMeta ? { ...getStartedView, _meta: { ...seoMeta } } : getStartedView;

  const [heroImage, setHeroImage] = useState(null);
  useEffect(() => {
    if (heroRef.current) {
      const heroDataAttributes = heroRef.current.dataset;

      if (typeof heroDataAttributes.heroImage !== 'undefined') {
        setHeroImage(heroDataAttributes.heroImage);
      }
    }
  }, [heroRef]);

  return (
    <ThemeProvider theme={{ ...theme, colorTone: themeColorTone ?? 'purple-blue' }}>
      <Head>
        <link rel="stylesheet" href={staticAssetUrl('/fonts/import-remote-sans.css')} />
        <link rel="stylesheet" href={staticAssetUrl('/fonts/import-bossa.css')} />
      </Head>
      <DocumentHead document={documentSeo} noIndex />
      <HeaderContext.Provider value={headerRef}>
        <Header ref={headerRef} hideBanner useRemoteFont>
          <NavigationPrimary>
            {headerNavigationItems.map(({ label, fragment }, index) => (
              <MenuEntry key={index}>
                <MenuItem as="a" href={fragment}>
                  {label}
                </MenuItem>
              </MenuEntry>
            ))}
          </NavigationPrimary>

          <NavigationSecondary>
            <TwoStepFormModalButton
              {...{
                hubspotFormDefinition: twoStepGetStartedHubspotFormDefinition,
                scheduleDemoReasonFields,
                promoCode,
                disablePrimaryCTAIcon,
              }}
              button={{
                as: HeaderCTAButton,
                $frenchLocale: frenchOrSpanishLocale,
                ...(heroImage && { tone: true }),
              }}
            >
              {headerTalkWithUsButtonLabel || 'Talk to us'}
            </TwoStepFormModalButton>
          </NavigationSecondary>
        </Header>
        <Main $themeColorTone={themeColorTone} ref={heroRef} $frenchLocale={frenchOrSpanishLocale}>
          <SiteHeader />
          {heroComponent || (
            <GetStartedHero
              $frenchLocale={frenchOrSpanishLocale}
              title={
                <>
                  {heroTitle} <CycleText entries={heroCycleEntriesText} />
                </>
              }
              accessibleTitle={accessibleTitle}
              description={
                calloutText ? (
                  <PrismicRichText field={calloutText} />
                ) : (
                  <HeroDescription>
                    <PrismicRichText field={heroDescription} />
                  </HeroDescription>
                )
              }
              aside={
                <TwoStepForm
                  formTitle={asText(getStartedFormTitle) || 'Start employing anywhere'}
                  formDescription={
                    asText(getStartedFormDescription) ||
                    "We'll contact you to schedule a call, answer any questions you may have, and start onboarding your employees."
                  }
                  hubspotFormDefinition={twoStepGetStartedHubspotFormDefinition}
                  scheduleDemoReasonFields={scheduleDemoReasonFields}
                  disablePrimaryCTAIcon={disablePrimaryCTAIcon}
                  companyEmailAddressesOnly
                />
              }
              actions={
                <>
                  {heroFeatures?.length > 0 && (
                    <GetStartedFeaturesList>
                      {heroFeatures.map(({ feature }, index) => (
                        <GetStartedFeaturesListEntry
                          key={index}
                          $frenchLocale={frenchOrSpanishLocale}
                        >
                          {feature}
                        </GetStartedFeaturesListEntry>
                      ))}
                    </GetStartedFeaturesList>
                  )}
                  <BadgesSection
                    badges={badges}
                    trustpilotRating={trustpilotRating}
                    frenchLocale={frenchOrSpanishLocale}
                  />
                </>
              }
              background={
                !frenchOrSpanishLocale && (
                  <>
                    <CustomHeroImage />
                    <HeroBackgroundIllustration alt="" />
                  </>
                )
              }
              hideOverflow
              image={heroBackgroundImage?.url}
            />
          )}
          <ClientLogosSlider {...clientLogosView} customTitle={clientLogosTitle} />
          <SlidesCarouselSection
            id="how-it-works"
            label={overviewLabel}
            title={overviewTitle}
            description={overviewDescription}
            slides={overviewSections}
            darkTone={frenchOrSpanishLocale}
            fontBrand
          />
          {frenchOrSpanishLocale ? (
            <>
              <NewTestimonials {...clientTestimonialsView} />
              <NewWhyRemoteSection {...{ ...whyRemotePrimary }} list={whyRemoteReasons} />
              <NewPricingSection
                id="pricing"
                label={pricingLabel}
                title={pricingTitle}
                hubspotFormDefinition={hubspotFormDefinition}
                pricingOverview={pricingOverview}
                frenchLocale
                learnMoreLabel={learnMoreLabel}
              />
              <PrimaryCTASection title={ctaTitle}>
                <TwoStepFormModalButton
                  {...{
                    hubspotFormDefinition: twoStepGetStartedHubspotFormDefinition,
                    scheduleDemoReasonFields,
                    promoCode,
                    disablePrimaryCTAIcon,
                  }}
                  button={{
                    as: CtaButton,
                    IconAfter: IconArrowRight,
                  }}
                >
                  {ctaButtonLabel}
                </TwoStepFormModalButton>
              </PrimaryCTASection>
            </>
          ) : (
            <>
              <Testimonials {...clientTestimonialsView} />
              <WhyRemoteSection
                {...{ ...whyRemotePrimary }}
                list={whyRemoteReasons}
                id="why-remote"
              />
              <PricingSectionRefreshed
                id="pricing"
                label={pricingLabel}
                title={pricingTitle}
                hubspotFormDefinition={hubspotFormDefinition}
                pricingOverview={pricingOverview}
              />

              <PrimaryCTA title={<>{ctaTitle}</>}>
                <TwoStepFormModalButton
                  {...{
                    hubspotFormDefinition: twoStepGetStartedHubspotFormDefinition,
                    scheduleDemoReasonFields,
                    promoCode,
                    disablePrimaryCTAIcon,
                  }}
                  button={{
                    as: PrimaryCTAButton,
                    IconAfter: IconArrowRight,
                  }}
                >
                  {ctaButtonLabel}
                </TwoStepFormModalButton>
              </PrimaryCTA>
            </>
          )}
        </Main>
      </HeaderContext.Provider>
    </ThemeProvider>
  );
}

GetStartedNewView.Layout = SinglePageScrollLayout;
